// @ts-check
// @ts-ignore

import { ganaToKana, kanaToGana } from './strings';
import { replaceObject, retrieveObject, parseMemberExpression } from './objects';

export const eventPath = (e) => {
  if ('composedPath' in e) return e.composedPath();
  if ('path' in e) return e.path;
  const path = [];
  let currentElem = e.target;
  while (currentElem) {
    path.push(currentElem);
    currentElem = currentElem.parentElement;
  }
  if (path.indexOf(window) === -1 && path.indexOf(document) === -1) path.push(document);
  if (path.indexOf(window) === -1) path.push(window);
  return path;
};

export const getFieldValue = (field, def = '') => {
  if (field.tagName === 'SELECT' && field.attributes['type']) {
    const type = field.attributes['type'].value;
    if (type === 'number')
      return field.value == '' ? def : Number(field.value);
    else
      return field.value ?? def;
  }
  switch (field.type) {
    case 'checkbox':
      return field.checked;
    case 'number':
      return field.value == '' ? def : Number(field.value);
    case 'text':
    case 'textarea':
      return field.value.trim();
    default:
      return field.value;
  }
};

export const getEventValue = (e, def = '') => {
  if (e instanceof Event) {
    /** @type {any} */
    const field = e.target;
    if (field.tagName === 'SELECT' && field.attributes['type']) {
      const type = field.attributes['type'].value;
      if (type === 'number')
        return field.value == '' ? def : Number(field.value);
      else
        return field.value ?? def;
    }
    switch (field.type) {
      case 'checkbox':
        return field.checked;
      case 'text':
      case 'textarea':
        return field.value.trim();
      default:
        return field.value;
    }
  }
  return e;
};

/**
 * @param {*} form
 * @return {*} 
 */
export const serializeForm = (form) => {
  const obj = {};
  const fields = form.elements;
  for (let i = 0; i < fields.length; i++) {
    const field = fields[i];
    if (!field.name) {
      continue;
    }

    const value = getFieldValue(field);
    if (obj[field.name] == null) {
      obj[field.name] = value;
    } else {
      if (!Array.isArray(obj[field.name])) {
        obj[field.name] = obj[field.name] ? [obj[field.name]] : [];
      }

      if (value != null) {
        obj[field.name].push(value);
      }
    }
  }
  return obj;
};

export const execCopy = async (value) => {
  try {
    if (navigator.clipboard != null && navigator.clipboard.write != null) {
      // Safariでは非同期になる場合はコピーがされない。対策として、Promiseを解決させずにvalueにそのまま渡す
      const type = 'text/plain';
      const blob = typeof value === 'string' ? new Blob([value], { type: 'text/plain' }) : value.then(text => new Blob([text], { type: 'text/plain' }));
      const item = new window.ClipboardItem({ [type]: blob });
      await navigator.clipboard.write([item]);
      return value;
    }
    const temp = document.createElement('textarea');
    temp.value = await Promise.resolve(value);
    const s = temp.style;
    s.position = 'fixed';
    s.left = '-100%';
    document.body.appendChild(temp);
    temp.focus();
    temp.selectionStart = 0;
    temp.selectionEnd = temp.value.length;
    document.execCommand('copy');
    temp.blur();
    document.body.removeChild(temp);
    return Promise.resolve(value);
  } catch (error) {
    return value;
  }
};

export const styleAttribute = (attributes) => {
  return Object.entries(attributes).reduce((acc, item) => {
    const [key, value] = item;
    return [...acc, `${key}: ${value}`];
  }, []).join(';');
};

export const getPosition = (e) => {
  const x = e.clientX == null ? e.x : e.clientX;
  const y = e.clientX == null ? e.y : e.clientY;
  return { x, y };
};

/**
 * @param {Node} node
 */
export const isNodeVisible = (node) => {
  if (node.parentElement == null) {
    return true;
  }
  return 0 < node.parentElement.offsetWidth && 0 < node.parentElement.offsetHeight;
};

const escapeRegExp = (string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};
/**
 * @param {Node} node
 * @param {string} text
 */
export const findTextNode = (node, text) => {
  const markList = [];
  const text1 = ganaToKana(text);
  const text2 = kanaToGana(text);
  const finalText = text.split('').map((_, i) => {
    if (text1[i] == text2[i]) {
      return escapeRegExp(text[i]);
    }
    return `(${escapeRegExp(text1[i])}|${escapeRegExp(text2[i])})`;
  }).join('');
  const regExp = new RegExp(finalText, 'gi');
  let match;
  if (node && node.nodeType == Node.TEXT_NODE && isNodeVisible(node) && (match = regExp.exec(node.nodeValue))) {
    const value = node.nodeValue;
    const ti = match.index;
    const startText = value.slice(0, ti);
    const endText = value.slice(ti + text.length, value.length);
    const markElement = document.createElement('mark');
    markList.push(markElement);
    const parentElement = node.parentElement;
    parentElement.replaceChild(markElement, node);
    markElement.appendChild(node);
    // @ts-ignore
    node.data = value.slice(ti, ti + text.length);
    if (startText != '') {
      parentElement.insertBefore(document.createTextNode(startText), markElement);
    }
    if (endText != '') {
      parentElement.insertBefore(document.createTextNode(endText), markElement.nextSibling);
    }
  }
  if (node && node.nodeType == Node.ELEMENT_NODE && !node.nodeName.toLowerCase().match(/^(mark|find-bar|textarea)$/)) {
    for (let i = 0; i < node.childNodes.length; i++) {
      const childNode = node.childNodes[i];
      const marks = findTextNode(childNode, text);
      markList.push(...marks);
    }
  }
  return markList;
};

export const cleanMarkElement = () => {
  const markElements = Array.from(document.querySelectorAll('mark'));
  markElements.forEach(node => {
    const parentElement = node.parentElement;
    if (parentElement == null) {
      return;
    }
    const startNode = node.previousSibling && node.previousSibling.nodeType == Node.TEXT_NODE ? node.previousSibling : null;
    const endNode = node.nextSibling && node.nextSibling.nodeType == Node.TEXT_NODE ? node.nextSibling : null;
    const text = `${startNode?.nodeValue || ''}${node.textContent}${endNode?.nodeValue || ''}`;
    if (startNode) {
      parentElement.removeChild(startNode);
    }
    if (endNode) {
      parentElement.removeChild(endNode);
    }
    const child = node.childNodes[0];
    parentElement.replaceChild(child, node);
    // @ts-ignore
    child.data = text;
    const pChildList = [...parentElement.childNodes.values()];
    for (let i = 0; i < pChildList.length; i++) {
      const ch = pChildList[i];
      if (ch.nodeName.toLowerCase() == 'mark') {
        // @ts-ignore
        child.data += ch.textContent;
        const startNode = ch.previousSibling && ch.previousSibling.nodeType == Node.TEXT_NODE ? ch.previousSibling : null;
        const endNode = ch.nextSibling && ch.nextSibling.nodeType == Node.TEXT_NODE ? ch.nextSibling : null;
        if (startNode && startNode != child) {
          parentElement.removeChild(startNode);
          // @ts-ignore
          child.data += startNode.textContent;
        }
        if (endNode && endNode != child) {
          parentElement.removeChild(endNode);
          // @ts-ignore
          child.data += endNode.textContent;
        }
        parentElement.removeChild(ch);
      }
    }
  });
};

/**
 * @param {string} selector 
 * @param {number} duration 
 * @param {Element} rootElement
 */
export const alignAnimation = (selector, duration, rootElement) => {
  const animatedControls = (rootElement ?? document)?.querySelectorAll(selector);
  // const startTime = new Date().getTime(); // get current timestamp
  // const animationDelay = duration - ((startTime % (duration * 1000)) / 1000); // calculate animation delay
  animatedControls?.forEach(control => {
    // @ts-ignore
    control.style.animationDelay = '0s'; //`-${animationDelay}s`; // set animation delay
  });
};

/**
 * 
 * @param {((val: any, target: string | string[]) => void) | undefined} [onGet] 
 * @param {((val: any, target: string | string[]) => void) | undefined} [onSet] 
 * @returns part of a Riot.js component with data binder functions
 */
export const useDataBinder = (onGet, onSet) => {

  const _get = (thisVar, target, postProcess) => {
    const val = retrieveObject(thisVar.state, parseMemberExpression(target), true);
    const ret = postProcess ? postProcess(val) : val;
    onGet?.bind(thisVar)?.(ret, target);
    return ret;
  };

  const _set = (thisVar, target, value, postProcess) => {
    const val = postProcess ? postProcess(value) : value;
    replaceObject(thisVar.state, parseMemberExpression(target), val, false, true, true);
    onSet?.bind(thisVar)?.(val, target);
  };

  return {
    /**
     * @param {string | string[]} target 
     * @param {((val: any) => any) | undefined} [postProcess]
     * @returns {any}
     */
    getState(target, postProcess) {
      return _get(this, target, postProcess);
    },

    setState(target, value, postProcess) {
      return _set(this, target, value, postProcess);
    },

    /**
     * @param {string | string[]} target 
     * @param {((val: any) => any) | undefined} [postProcess]
     * @returns 
     */
    handleChange(target, postProcess) {
      return e => {
        const val = getEventValue(e);
        _set(this, target, val, postProcess);
        this.update();
      };
    },

    /**
     * @param {string | string[]} target 
     * @param {string | undefined} [keyGet]
     * @param {string | undefined} [keySet]
     * @param {((val: any) => any) | undefined} [postProcessGet]
     * @param {((val: any) => any) | undefined} [postProcessSet]
     */
    bind(target, keyGet, keySet, postProcessGet, postProcessSet) {
      keyGet = keyGet || 'value';
      keySet = keySet || 'onchange';
      const targetArray = parseMemberExpression(target);
      return {
        [keyGet]: this.getState(targetArray, postProcessGet),
        [keySet]: this.handleChange(targetArray, postProcessSet),
      };
    }
  };

};